'use client';

import { useEffect, useState } from 'react';

/**
 * Loads the Google Maps JS API (with Places) on first use instead of on every
 * page from the root layout — most pages never touch it, and the ~250 KB
 * script competed with hydration. Every Maps consumer awaits this; the script
 * is injected once and shared.
 */

const CALLBACK = '__hdGoogleMapsReady';

let loading: Promise<void> | null = null;

const isLoaded = () =>
    typeof window !== 'undefined' && !!window.google?.maps?.places;

export function loadGoogleMaps(): Promise<void> {
    if (isLoaded()) return Promise.resolve();
    if (loading) return loading;

    loading = new Promise<void>((resolve, reject) => {
        (window as any)[CALLBACK] = () => resolve();
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_FIREBASE_API_KEY}&libraries=places&callback=${CALLBACK}`;
        script.async = true;
        script.onerror = () => {
            // Let the next caller retry (flaky network, blocked once).
            loading = null;
            script.remove();
            reject(new Error('Google Maps failed to load'));
        };
        document.head.appendChild(script);
    });
    return loading;
}

/** Start loading ahead of need (e.g. search box focus); errors are ignored
 *  here and surface to whoever actually awaits the API. */
export function preloadGoogleMaps(): void {
    loadGoogleMaps().catch(() => undefined);
}

/** `true` once the Maps API is available; triggers the load on mount. */
export function useGoogleMapsLoaded(): boolean {
    const [loaded, setLoaded] = useState(isLoaded);
    useEffect(() => {
        if (loaded) return;
        let active = true;
        loadGoogleMaps()
            .then(() => active && setLoaded(true))
            .catch((err) => console.error(err));
        return () => {
            active = false;
        };
    }, [loaded]);
    return loaded;
}
